import { ClickAwayListener, Drawer } from '@material-ui/core';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Button } from '../../../../components/button/Button';
import { FlatButton } from '../../../../components/button/FlatButton';
import { DateInput } from '../../../../components/form-elements/DateInput';
import { LocalTimeRead } from '../../../../models/utilities/local-time-read';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { StatusCode } from '../../../../utils/api';
import { TransportationUtils } from '../../../helpers';
import { ShipmentActionType } from '../../../models/enums/shipment-action-type';
import { ShipmentType } from '../../../models/enums/shipment-type';
import { DeliveryShipmentAction, ShipmentAction, TravelShipmentAction } from '../../../models/shipments/shipment-action';
import { TransportationSvc } from '../../../service';
import { RouteDisplay } from '../../mapping/RouteDisplay';
import { ShipmentActionScheduleRow } from '../ShipmentActionScheduleRow';

interface IProps {
    visible: boolean;
    onSuccess: any;
    onClose: any;
    stopList: ShipmentAction[];
    entityId: number;
    entityVersion: string;
    entityType: ShipmentType;
}

export const ActionSetSchedule = (props: IProps) => {

    const [service] = React.useState(new TransportationSvc());
    const [list, setList] = React.useState<ShipmentAction[]>(props.stopList);
    const [showDropdown, setShowDropDown] = React.useState(false);
    const [selectedStopId, setSelectedStopId] = React.useState(0);
    const [totalDistance, setTotalDistance] = React.useState(0);
    const [totalTravel, setTotalTravel] = React.useState('00:00:00');
    const [moveByDate, setMoveByDate] = React.useState<Date|undefined>(undefined);
    const [showMoveBy, setShowMoveBy] = React.useState(false);

    /**
     * On load, sort out the origin and destination from the list
     * so that they can't be dragged
     */
    React.useEffect(() => {
        if (props.stopList) {
            setList([...props.stopList]);
            setTotalDistance(TransportationUtils.computeTotalDistance(props.stopList));
            setTotalTravel(TransportationUtils.computeTotalTravelTime(props.stopList));

            // If we have an origin stop time, prepopulate the date picker value
            // for moving the date.
            const originTime = _getOriginStopTime(props.stopList);
            if (originTime) {
                setMoveByDate(originTime.local);
            }

        }
    }, [props.stopList]);

    const hideDropDown = () => {
        setShowDropDown(false);
    };

    const toggleDropDown = () => {
        setShowDropDown(!showDropdown);
    };

    /**
     * Update the list order based on the user's drag and drop
     * @param result The reordered item
     */
    const onDragEnd = (result: DropResult) => {

        if (!result.destination) {
            return;
        }

        if ((result.destination.droppableId === result.source.droppableId) &&
            (result.destination.index === result.source.index)) {
            return;
        }

        // Our .index properties here are operating in the scope of the
        // droppable which does not include the origin and destination
        // stops. but newList is all stops.  The indicies need adjusted 
        // up 1 to accomodate the origin stop that is in the list, but 
        // never tampered with.

        const newList = [...list];
        const [removed] = newList.splice(result.source.index + 1, 1);
        newList.splice(result.destination.index + 1, 0, removed);

        // Reset the sequence IDs
        newList.forEach((itm: ShipmentAction, idx: number) => {
            itm.index = idx + 1;
        });

        setList(newList);
    };

    /**
     * Handle the shipment time change for a stop
     * @param stopId The stop ID we're editing
     * @param type The kind of stop we're editing
     * @param value The new value to set
     */
    const onShipmentTimeChange = (stopId: number, type: ShipmentActionType, value: any) => {
        const tmpList = [...list];
        tmpList.forEach((itm: ShipmentAction) => {
            if (itm.id === stopId) {

                // For new stops without a time set, this property
                // will be undefined, so we will new it up first
                if (!itm.actionTime) {
                    itm.actionTime = new LocalTimeRead();
                }

                itm.actionTime.local = value;
            }
        });

        setList(tmpList);        
    };

    /**
     * Handle the travel time change for a stop
     * @param stopId The stop ID we're editing
     * @param type The kind of stop we're editing
     * @param value The new value to set
     */
    const onTravelTimeChange = (stopId: number, type: ShipmentActionType, e: any) => {
        const tmpList = [...list];
        tmpList.forEach((itm: ShipmentAction) => {
            if (itm.id === stopId && itm.type !== ShipmentActionType.Origin) {
                (itm as TravelShipmentAction).travelTime = e.target.value;
            }
        });

        setTotalTravel(TransportationUtils.computeTotalTravelTime(props.stopList));
        setList(tmpList);
    };

    /**
     * Handle the distance change for a stop
     * @param stopId The stop ID we're editing
     * @param type The kind of stop we're editing
     * @param value The new value to set
     */
    const onDistanceTimeChange = (stopId: number, type: ShipmentActionType, e: any) => {
        const tmpList = [...list];
        tmpList.forEach((itm: ShipmentAction) => {
            if (itm.id === stopId && itm.type !== ShipmentActionType.Origin) {
                (itm as TravelShipmentAction).distance = Number.parseInt(e.target.value, 10);
            }
        });

        setTotalDistance(TransportationUtils.computeTotalDistance(props.stopList));
        setList(tmpList);
    };

    /**
     * Change the background of the item being dragged
     * @param isDragging Boolean for whether the element is being dragged
     * @param draggableStyle Additional drag props
     */
    const getItemStyle = (isDragging: any, draggableStyle: any) => ({
        background: isDragging ? "#AADFFD" : "#FFFFFF",
        ...draggableStyle
    });

    /**
     * Save the schedule back to the server, and if successful
     * close the flyout
     */
    const onSave = async () => {

        const result = await service.setSchedule(
            list,
            props.entityId,
            props.entityVersion,
            props.entityType
        );

        if (result.status === StatusCode.Success) {
            setList([]);
            props.onSuccess();
        }
    };

    /**
     * Make a request to fetch distance and travel times, plus
     * optionally re-sequence them
     * @param optimize shoudl the request also optimize the route
     */
    const calculateRoute = async (optimize: boolean) => {

        setShowDropDown(false);

        const result = await service.calculateRoute(
            list,
            optimize,
            props.entityType
        );

        if (result.status === StatusCode.Success) {

            let tmpList = [...list];
            tmpList.forEach((stop: ShipmentAction) => {
                for (let i = 0; i < result.data.stops.length; i++) {
                    const itm = result.data.stops[i];
                    if (itm.sequenceId === stop.index) {
                        stop.index = i;

                        if (stop.type !== ShipmentActionType.Origin) {
                            (stop as TravelShipmentAction).distance = itm.distance;
                            (stop as TravelShipmentAction).travelTime = itm.travelTime;
                        }
                        break;
                    }
                }
            });

            const compareFn = (a: ShipmentAction, b: ShipmentAction) => {
                if (a.index < b.index) {
                    return -1;
                }
                if (a.index > b.index) {
                    return 1;
                }
                // a must be equal to b
                return 0;
            };

            tmpList = tmpList.sort(compareFn);
            setList(tmpList);
            setTotalDistance(TransportationUtils.computeTotalDistance(tmpList));
            setTotalTravel(TransportationUtils.computeTotalTravelTime(tmpList));
        }
    };

    /**
     * Cleanup our state and close
     */
    const onCleanup = () => {
        setList([]);
        setShowMoveBy(false);
        setMoveByDate(undefined);
        props.onClose();
    };

    const getStopCoordinates = (itm: ShipmentAction) => {
        (selectedStopId === itm.id) ? setSelectedStopId(0) : setSelectedStopId(itm.id);
    };

    const onMapChildClick = (key : any) => {
        
        const route = list.find((itm : ShipmentAction) => {
            return itm.id.toString() === key;
        });

        if (route) {
            getStopCoordinates(route);
        }
    };

    const canMoveAll = () => {        
        return list.some((itm : ShipmentAction) => {
            return (itm.type === ShipmentActionType.Origin) && 
                   (itm.actionTime !== undefined) && 
                   (itm.actionTime.local);
        });
    };

    const showUpdateAllFauxdal = () => {
        hideDropDown();
        setShowMoveBy(true);
    };

    const moveAll = () => {

        const tmpList = [...list];
        const originTime = _getOriginStopTime(tmpList);

        if (originTime && moveByDate) {

            const timeDiff = moveByDate.getTime() - originTime.local.getTime();
            const dayDiff = timeDiff / (1000 * 3600 * 24);
            let newOrigin : Date|undefined;

            // Adjust the dates by that much
            tmpList.forEach((stop: ShipmentAction) => {
                if (stop.actionTime) {                
                    const newDate = new Date(stop.actionTime.local);
                    newDate.setDate(newDate.getDate() + dayDiff);
                    stop.actionTime.local = newDate;

                    if (stop.type === ShipmentActionType.Origin) {
                        newOrigin = newDate;
                    }
                }
            });

            setMoveByDate(newOrigin);
        }

        setList(tmpList);
        setShowMoveBy(false);
    };

    const _getOriginStopTime = (listVals : ShipmentAction[]) : LocalTimeRead|undefined => {
        const origin = listVals.find((stop : ShipmentAction) => {
            return stop.type === ShipmentActionType.Origin;
        });

        return origin && origin.actionTime ? origin.actionTime : undefined;
    };

    return <Drawer
        anchor="right"
        onClose={onCleanup}
        open={props.visible}>
        <div className="stop-action-drawer large stop-action-reorder-route">

            <div className="schedule-wrapper">

                <div className="stop-list">

                    <div className="stop-list-header">
                        <h1>Set Schedule</h1>
                        {
                            props.entityType === ShipmentType.Truck
                                ? <ClickAwayListener
                                    key="stop-schedule-actions-clickaway-1"
                                    onClickAway={hideDropDown}>
                                    <span className="combo-create-widget">
                                        <button
                                            className="btn"
                                            onClick={toggleDropDown}>
                                            Options
                                                    <i className="la la-angle-down" />
                                        </button>
                                        {
                                            showDropdown
                                                ? <div className="dropdown-menu show">
                                                    <FlatButton
                                                        id="addActivityBtn"
                                                        text="Recalculate"
                                                        icon="la-plus-circle"
                                                        onBtnClick={() => calculateRoute(false)}
                                                    />
                                                    <FlatButton
                                                        id="setScheduleBtn"
                                                        text="Optimize"
                                                        icon="la-calendar"
                                                        onBtnClick={() => calculateRoute(true)}
                                                    />
                                                    <FlatButton
                                                        id="updateAllBtn"
                                                        text="Update All"
                                                        icon="la-mail-forward"
                                                        disable={ !canMoveAll() }
                                                        onBtnClick={ showUpdateAllFauxdal }
                                                    />
                                                </div>
                                                : null
                                        }
                                    </span>
                                </ClickAwayListener>
                                : <Button
                                    id="updateAllBtn"
                                    testID="updateAllBtn"
                                    text="Update All"
                                    icon="la-mail-forward"
                                    disable={ !canMoveAll() }
                                    onBtnClick={ showUpdateAllFauxdal }
                                />
                        }
                    </div>

                    {
                        list && list.length > 0
                            ? <React.Fragment>

                                <ShipmentActionScheduleRow
                                    item={ list[0] }
                                    selectedStopId={ selectedStopId }
                                    idx={ 0 }
                                    onClick={ getStopCoordinates }
                                    entityType={ props.entityType }
                                    onShipmentTimeChange={ onShipmentTimeChange }
                                    onDistanceTimeChange={ onDistanceTimeChange }
                                    onTravelTimeChange={ onTravelTimeChange } 
                                />
                                
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="routeList">
                                        {
                                            (provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    className="truck-route-droppable"
                                                    {...provided.droppableProps}>
                                                    {
                                                        list.filter((item : ShipmentAction) => {
                                                            return item.type === ShipmentActionType.Delivery || item.type === ShipmentActionType.Backhaul;
                                                        }).map((item: ShipmentAction, index) => (
                                                            <Draggable
                                                                key={item.id}
                                                                draggableId={item.id.toString()}
                                                                index={index}>
                                                                {
                                                                    (p, s) => (
                                                                        <div
                                                                            className="draggable-wrapper"
                                                                            ref={p.innerRef}
                                                                            {...p.draggableProps}
                                                                            {...p.dragHandleProps}
                                                                            style={getItemStyle(s.isDragging, p.draggableProps.style)}>
                                                                                <ShipmentActionScheduleRow
                                                                                    item={ item }
                                                                                    selectedStopId={ selectedStopId }
                                                                                    idx={ index }
                                                                                    entityType={ props.entityType }
                                                                                    onClick={ getStopCoordinates }
                                                                                    onShipmentTimeChange={ onShipmentTimeChange }
                                                                                    onDistanceTimeChange={ onDistanceTimeChange }
                                                                                    onTravelTimeChange={ onTravelTimeChange } 
                                                                                />
                                                                                {
                                                                                    item.type === ShipmentActionType.Delivery
                                                                                        ? <div className="est-unload-time">
                                                                                            <p><i className="la la-truck"/> Est. Unload Time: { (item as DeliveryShipmentAction).unloadTime }</p>
                                                                                        </div>
                                                                                        : null
                                                                                }
                                                                        </div>
                                                                    )}
                                                            </Draggable>
                                                        ))
                                                    }
                                                    {provided.placeholder}
                                                </div>
                                            )
                                        }
                                    </Droppable>
                                </DragDropContext>

                                {
                                    list[list.length - 1].type === ShipmentActionType.Destination
                                        ?  <ShipmentActionScheduleRow
                                            item={ list[list.length - 1] }
                                            selectedStopId={ selectedStopId }
                                            idx={ 0 }
                                            entityType={ props.entityType }
                                            onClick={ getStopCoordinates }
                                            onShipmentTimeChange={ onShipmentTimeChange }
                                            onDistanceTimeChange={ onDistanceTimeChange }
                                            onTravelTimeChange={ onTravelTimeChange } 
                                        />
                                        : null
                                }                        
                            </React.Fragment>
                            : null
                    }

                    {
                        props.entityType === ShipmentType.Truck
                            ? <div className="schedule-totals">
                                <p>
                                    <span>Total Distance: </span> { totalDistance }
                                </p>
                                <p>
                                    <span>Total Time: </span> { totalTravel }
                                </p>
                            </div>
                            : null
                    }
                </div>

                <div className="stop-map">
                    <RouteDisplay
                        allStops={ list }
                        selectedID={ selectedStopId }
                        showRoute={ props.entityType === ShipmentType.Truck }
                        onMarkerClick={ onMapChildClick }
                    />                    
                </div>
            </div>

            {
                showMoveBy
                    ? <div className="moveall-schedule-wrap">
                        <div className="moveall-schedule-content">
                            <h3>Change Departure Date</h3>
                            <p className="mt-3">Select a new departure date using the input below.  All stops will be moved forward or backwards based on this new date.</p>
                            <p>Any stops without a time will remain blank.</p>
                            <p className="pb-3 font-italic">
                                (For example: changing the departure date from the 1st to the 3rd will move all stop times forward by 2 days.)
                            </p>                            
                            <DateInput
                                id="change-departure-date-input"
                                onChange={(e: any) => setMoveByDate(e) }
                                value={ moveByDate }
                                label="New Departure Date"
                                placeholder=""
                            />

                            <div className="moveall-schedule-buttons">
                                <Button
                                    testID="schedule-updateall"
                                    text="Update All"
                                    onBtnClick={ moveAll }
                                />
                                <Button
                                    testID="schedule-updateall"
                                    text="Close"
                                    onBtnClick={ () => setShowMoveBy(false) }
                                />
                            </div>
                        </div>
                    </div>
                    : null
            }   

            <FlyoutSaveCancel
                onSave={onSave}
                onCancel={onCleanup}
            />

        </div>
    </Drawer>;
};
